import { UserContext, User } from "@/shared/lib/context/UserContext";
import React, { useMemo, useState, ReactNode, useEffect, useCallback } from "react";
import { USER_LOCALSTORAGE_KEY, AUTH_TOKEN_KEY } from "@/shared/config/localstorage";
import { getSessionToken } from "@/features/bucket/api";
import {
    getAccountIdentity,
    resetIdentityCache,
} from "@/features/identity/api/identityApi";
import { isPlaceholderName } from "@/shared/lib/isPlaceholderName";
import { activityLog } from "@/features/activityLog";
import { clearGoalShelf } from "@/features/achievements/lib/goalShelfKey";

export interface IUserProvider {
    children: ReactNode;
}

const DEV_ADMIN_USER: User = {
    id: "664b0c4e2df14445",
    email: "vk_29650451",
    name: "Dev Admin",
};

export const UserProvider = ({ children }: IUserProvider) => {
    const isDev = process.env.REACT_APP_DEV_MODE === "true";

    const [user, setUserState] = useState<User | string>(() => {
        if (isDev) return DEV_ADMIN_USER;
        const stored = localStorage.getItem(USER_LOCALSTORAGE_KEY);
        if (stored) {
            try {
                return JSON.parse(stored);
            } catch {
                return stored;
            }
        }
        return "";
    });

    const DEV_TOKEN = "dev-admin-token";

    const [token, setTokenState] = useState<string>(() => {
        if (isDev) {
            localStorage.setItem(AUTH_TOKEN_KEY, DEV_TOKEN);
            return DEV_TOKEN;
        }
        return localStorage.getItem(AUTH_TOKEN_KEY) || "";
    });

    useEffect(() => {
        if (user) {
            if (typeof user === "string") {
                localStorage.setItem(USER_LOCALSTORAGE_KEY, user);
            } else {
                localStorage.setItem(USER_LOCALSTORAGE_KEY, JSON.stringify(user));
            }
        }
    }, [user]);

    useEffect(() => {
        if (token) {
            localStorage.setItem(AUTH_TOKEN_KEY, token);
        }
    }, [token]);

    // Recover the name and the photo Google gave us at some earlier login —
    // possibly in the phone app, possibly in a browser this one has never
    // shared storage with. Without this, a person who signed in anywhere else
    // still shows up here as the front of their email address.
    //
    // Only ever FILLS IN: anything the person typed themselves wins, so a real
    // local name and a chosen photo are left untouched. Lives here rather than
    // in App because this is where the user state is owned — the functional
    // update below reads the user as it is when the worker answers, without a
    // subscription or a ref.
    useEffect(() => {
        if (isDev) return;
        // Nothing to ask with: a plain visitor, or a login older than the
        // session tokens. Skipping the request keeps the worker out of the
        // boot path for everyone who is not signed in.
        if (!getSessionToken()) return;
        let alive = true;
        void getAccountIdentity().then((identity) => {
            if (!alive || !identity) return;
            if (identity.name && isPlaceholderName(localStorage.getItem("site_user_name"))) {
                localStorage.setItem("site_user_name", identity.name);
            }
            // Google's own URL, not the worker's /account/photo: this is stored
            // and re-read across reloads, and /account/photo needs a Bearer
            // header no <img> can send.
            if (identity.googlePicture && !localStorage.getItem("site_user_picture")) {
                localStorage.setItem("site_user_picture", identity.googlePicture);
            }
            setUserState((current) => {
                if (!current || typeof current === "string") return current;
                const patch: Partial<User> = {};
                if (identity.name && isPlaceholderName(current.name)) {
                    patch.name = identity.name;
                }
                if (identity.googlePicture && !current.picture) {
                    patch.picture = identity.googlePicture;
                }
                return Object.keys(patch).length > 0 ? { ...current, ...patch } : current;
            });
        });
        return () => {
            alive = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const setUser = useCallback((newUser: User | string) => {
        setUserState(newUser);
    }, []);

    const setToken = useCallback((newToken: string) => {
        setTokenState(newToken);
    }, []);

    const logout = useCallback(() => {
        // The activity log sends its last batch with the token that is about to go.
        activityLog.noteLogout("logout");
        // Before the keys go: the identity cache is keyed on site_user_email
        // and would otherwise keep this person's name for the next login.
        resetIdentityCache();
        setUserState("");
        setTokenState("");
        localStorage.removeItem(USER_LOCALSTORAGE_KEY);
        localStorage.removeItem(AUTH_TOKEN_KEY);
        localStorage.removeItem("account_uid");
        localStorage.removeItem("site_user_email");
        localStorage.removeItem("site_user_name");
        // The avatar is as personal as the name — leaving it behind would show
        // the previous person's face to whoever logs in next on this browser.
        localStorage.removeItem("site_user_picture");
        localStorage.removeItem("site_user_phone");
        localStorage.removeItem("site_vk_id");
        localStorage.removeItem("site_sber_id");
        // Its own next achievement goals (the hint rows' shelf) are per person too.
        clearGoalShelf();
        // Wipe per-user asked-history buckets so the next login on the
        // same browser does not surface this user's questions to whoever
        // logs in next (issue #112).
        try {
            const keysToDrop: string[] = [];
            for (let i = 0; i < localStorage.length; i++) {
                const k = localStorage.key(i);
                if (
                    k &&
                    (k === "asked_questions_history" ||
                        k.startsWith("asked_questions_history__"))
                ) {
                    keysToDrop.push(k);
                }
            }
            for (const k of keysToDrop) localStorage.removeItem(k);
        } catch {
            // ignore — best effort
        }
        // Drop per-user course progress sessionStorage cache so the next
        // login on this tab does not see the previous user's progress
        // (issue #454).
        try {
            const keysToDrop: string[] = [];
            for (let i = 0; i < sessionStorage.length; i++) {
                const k = sessionStorage.key(i);
                if (k && k.startsWith("course_progress_")) {
                    keysToDrop.push(k);
                }
            }
            for (const k of keysToDrop) sessionStorage.removeItem(k);
        } catch {
            // ignore — best effort
        }
    }, []);

    const isAuthenticated = useMemo(() => {
        if (typeof user === "string") {
            return !!user;
        }
        return !!user?.id;
    }, [user]);

    const defaultProps = useMemo(
        () => ({
            user,
            token,
            setUser,
            setToken,
            logout,
            isAuthenticated,
        }),
        [user, token, setUser, setToken, logout, isAuthenticated]
    );

    return <UserContext value={defaultProps}>{children}</UserContext>;
};
